import React, { useCallback } from 'react';

interface Props<T extends HTMLElement> {
  /** React ref pointing to the element to scroll into view */
  el: React.RefObject<T>;
  /** Scroll behavior; defaults to `'smooth'` */
  behavior?: ScrollBehavior;
  /** Vertical alignment of the element after scrolling; defaults to `'center'` */
  block?: ScrollLogicalPosition;
  /** Horizontal alignment of the element after scrolling; defaults to `'nearest'` */
  inline?: ScrollLogicalPosition;
  /**
   * Focus an element after scrolling. Pass `true` to focus the scrolled element
   * itself, or a ref to focus a different element (e.g. the nested input of a
   * composite control whose wrapper is what gets scrolled). Defaults to no
   * focus. Focus is applied with `preventScroll` so it doesn't fight the
   * scroll above.
   */
  focus?: boolean | React.RefObject<HTMLElement | null>;
}

/**
 * Returns a stable callback that scrolls the referenced element into view and,
 * optionally, focuses it (or a related element).
 *
 * Lazily reads `el.current` (and the focus ref) at call time, so refs don't
 * need to be attached when the hook runs — only when the returned callback
 * fires. The callback is a no-op if the element isn't mounted.
 *
 * @example
 * // Scroll only
 * const ref = useRef<HTMLDivElement>(null);
 * const makeVisible = useScrollIntoView({ el: ref });
 *
 * @example
 * // Scroll then focus the same element
 * const makeVisible = useScrollIntoView({ el: ref, focus: true });
 *
 * @example
 * // Scroll a wrapper, focus a nested input (e.g. a composite Select)
 * const makeVisible = useScrollIntoView({ el: scrollRef, focus: inputRef });
 */
export const useScrollIntoView = <T extends HTMLElement>({
  el,
  behavior = 'smooth',
  block = 'center',
  inline = 'nearest',
  focus = false,
}: Props<T>): (() => void) =>
  useCallback(() => {
    const element = el.current;
    if (!element) {
      return;
    }

    element.scrollIntoView({ behavior, block, inline });

    if (!focus) {
      return;
    }
    const focusTarget = focus === true ? element : focus.current;
    // `preventScroll` so focus doesn't fight the smooth scroll above.
    focusTarget?.focus({ preventScroll: true });
  }, [el, behavior, block, inline, focus]);
